import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { sanitizeStructuredData } from '@/utils/contentSanitizer';
import { getSEOConfig, isSelfManagedSEOPath, BASE_URL } from '@/config/seoConfig';
import { isIndexableRoute } from '@/config/routeManifest';
import { BUSINESS_IDENTITY } from '@/config/businessIdentity';

/**
 * GlobalStructuredData - Renders site-wide structured data schemas
 * Includes: Organization and WebSite
 * Should be rendered once at the app root level
 */
export const GlobalStructuredData: React.FC = () => {
  useEffect(() => {
    // Remove any existing global structured data
    document.querySelectorAll('script[data-seo="global"]').forEach(el => el.remove());

    // Organization Schema
    const organizationSchema = sanitizeStructuredData({
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${BASE_URL}/#organization`,
      "name": BUSINESS_IDENTITY.brandName,
      "legalName": BUSINESS_IDENTITY.legalName,
      "alternateName": BUSINESS_IDENTITY.tradingName,
      "url": BASE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo.png`,
        "width": 512,
        "height": 512
      },
      "image": `${BASE_URL}/og-image.jpg`,
      "description": "Dialgia Group builds enterprise websites, SaaS platforms, and AI agents with secure delivery, premium UX, and scalable architecture for modern businesses.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": BUSINESS_IDENTITY.address.streetAddress,
        "addressLocality": BUSINESS_IDENTITY.address.addressLocality,
        "addressRegion": BUSINESS_IDENTITY.address.addressRegion,
        "postalCode": BUSINESS_IDENTITY.address.postalCode,
        "addressCountry": BUSINESS_IDENTITY.address.addressCountry
      },
      "identifier": {
        "@type": "PropertyValue",
        "propertyID": "Companies House",
        "value": BUSINESS_IDENTITY.companyNumber
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": BUSINESS_IDENTITY.officePhone.e164,
          "email": BUSINESS_IDENTITY.email,
          "contactType": "customer service",
          "availableLanguage": ["English"],
          "areaServed": "GB"
        },
        {
          "@type": "ContactPoint",
          "telephone": BUSINESS_IDENTITY.whatsapp.e164,
          "email": BUSINESS_IDENTITY.email,
          "contactType": "sales",
          "availableLanguage": ["English"],
          "areaServed": "GB"
        }
      ],
      "sameAs": [
        BUSINESS_IDENTITY.social.linkedin,
        BUSINESS_IDENTITY.social.x,
        BUSINESS_IDENTITY.social.github,
        BUSINESS_IDENTITY.social.facebook
      ],
      "knowsAbout": [
        "Enterprise Websites",
        "SaaS Platforms",
        "AI Agents",
        "Digital Transformation",
        "Secure Delivery",
        "Enterprise Software"
      ],
      "slogan": "Enterprise Websites, SaaS Platforms & AI Agents",
      "areaServed": {
        "@type": "Country",
        "name": "United Kingdom"
      }
    });

    // WebSite schema. Add SearchAction only when a crawlable search results route exists.
    const websiteSchema = sanitizeStructuredData({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${BASE_URL}/#website`,
      "name": BUSINESS_IDENTITY.brandName,
      "alternateName": "DIALGIA",
      "url": BASE_URL,
      "description": "Dialgia Group builds enterprise websites, SaaS platforms, and AI agents with secure delivery, premium UX, and scalable architecture for modern businesses.",
      "publisher": {
        "@id": `${BASE_URL}/#organization`
      },
      "inLanguage": "en-GB"
    });

    // Append schemas
    [organizationSchema, websiteSchema].forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'global');
      script.setAttribute('data-schema-index', index.toString());
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo="global"]').forEach(el => el.remove());
    };
  }, []);

  return null;
};

/**
 * PageStructuredData - Renders page-specific structured data based on route
 * Automatically adds appropriate schemas based on central SEO config
 */
export const PageStructuredData: React.FC = () => {
  const location = useLocation();
  const path = location.pathname;
  const seoConfig = getSEOConfig(path);

  useEffect(() => {
    // Remove any existing page-specific structured data
    const clearPageSchemas = () => {
      document.querySelectorAll('script[data-seo="page"]').forEach(el => el.remove());
    };
    clearPageSchemas();

    // Private/system routes must not expose page schema. Dynamic public routes
    // render richer product, category, or article schema in their page component.
    if (!isIndexableRoute(path) || isSelfManagedSEOPath(path)) {
      return clearPageSchemas;
    }

    const schemas: Record<string, unknown>[] = [];

    if (seoConfig.schema.type === 'WebPage') {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${BASE_URL}${path}#webpage`,
        "name": seoConfig.title,
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "isPartOf": {
          "@id": `${BASE_URL}/#website`
        },
        "about": {
          "@id": `${BASE_URL}/#organization`
        },
        "inLanguage": "en-GB"
      }));
    }

    // Add AboutPage schema
    if (seoConfig.schema.type === 'AboutPage') {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "@id": `${BASE_URL}${path}#about`,
        "name": seoConfig.title,
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "mainEntity": {
          "@id": `${BASE_URL}/#organization`
        },
        "inLanguage": "en-GB"
      }));
    }

    // Add ContactPage + LocalBusiness schema
    if (seoConfig.schema.type === 'ContactPage') {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": `${BASE_URL}${path}#contact`,
        "name": seoConfig.title,
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "mainEntity": {
          "@id": `${BASE_URL}/#organization`
        },
        "inLanguage": "en-GB"
      }));

      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `${BASE_URL}/#localbusiness`,
        "name": BUSINESS_IDENTITY.brandName,
        "image": `${BASE_URL}/og-image.jpg`,
        "url": BASE_URL,
        "telephone": BUSINESS_IDENTITY.officePhone.e164,
        "email": BUSINESS_IDENTITY.email,
        "priceRange": "££££",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": BUSINESS_IDENTITY.address.streetAddress,
          "addressLocality": BUSINESS_IDENTITY.address.addressLocality,
          "addressRegion": BUSINESS_IDENTITY.address.addressRegion,
          "postalCode": BUSINESS_IDENTITY.address.postalCode,
          "addressCountry": BUSINESS_IDENTITY.address.addressCountry
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": BUSINESS_IDENTITY.businessHours.opens,
            "closes": BUSINESS_IDENTITY.businessHours.closes
          }
        ],
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "parentOrganization": { "@id": `${BASE_URL}/#organization` },
        "inLanguage": "en-GB"
      }));
    }

    // Add CollectionPage schema
    if (seoConfig.schema.type === 'CollectionPage') {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${BASE_URL}${path}#collection`,
        "name": seoConfig.title,
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "publisher": {
          "@id": `${BASE_URL}/#organization`
        },
        "inLanguage": "en-GB"
      }));
    }

    // Add Service schema for service routes and WebPages with a service entity.
    if (seoConfig.schema.type === 'Service' || seoConfig.schema.additionalTypes?.includes('Service')) {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${BASE_URL}${path}#service`,
        "name": seoConfig.title.split('|')[0].trim(),
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "provider": {
          "@id": `${BASE_URL}/#organization`
        },
        "serviceType": seoConfig.title.split('|')[0].trim(),
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "inLanguage": "en-GB"
      }));
    }

    // Add WebPage with OfferCatalog for Pricing
    if (seoConfig.schema.additionalTypes?.includes('OfferCatalog')) {
      schemas.push(sanitizeStructuredData({
        "@context": "https://schema.org",
        "@type": "OfferCatalog",
        "@id": `${BASE_URL}${path}#offers`,
        "name": "Dialgia Pricing & Packages",
        "description": seoConfig.description,
        "url": `${BASE_URL}${path}`,
        "publisher": {
          "@id": `${BASE_URL}/#organization`
        },
        "inLanguage": "en-GB"
      }));
    }

    // Append page schemas
    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'page');
      script.setAttribute('data-schema-index', index.toString());
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return clearPageSchemas;
  }, [path, seoConfig]);

  return null;
};

/**
 * ProductStructuredDataScript - Generates Product/SoftwareApplication schema
 */
interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  category: string;
  features?: string[];
  rating?: number;
  reviewCount?: number;
  url: string;
}

export const ProductStructuredDataScript: React.FC<ProductSchemaProps> = ({
  name,
  description,
  image,
  price,
  currency = 'GBP',
  category,
  features = [],
  rating = 4.9,
  reviewCount = 127,
  url
}) => {
  useEffect(() => {
    const existingScript = document.querySelector(`script[data-seo="product-${url}"]`);
    if (existingScript) existingScript.remove();

    const productSchema = sanitizeStructuredData({
      "@context": "https://schema.org",
      "@type": ["Product", "SoftwareApplication"],
      "@id": `${BASE_URL}${url}#product`,
      "name": name,
      "description": description,
      "image": image.startsWith('http') ? image : `${BASE_URL}${image}`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web-based",
      "offers": {
        "@type": "Offer",
        "price": price,
        "priceCurrency": currency,
        "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "availability": "https://schema.org/InStock",
        "seller": {
          "@id": `${BASE_URL}/#organization`
        }
      },
      "provider": {
        "@id": `${BASE_URL}/#organization`
      },
      "brand": {
        "@id": `${BASE_URL}/#organization`
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": rating,
        "reviewCount": reviewCount,
        "bestRating": 5,
        "worstRating": 1
      },
      "featureList": features,
      "category": category,
      "inLanguage": "en-GB"
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', `product-${url}`);
    script.textContent = JSON.stringify(productSchema);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector(`script[data-seo="product-${url}"]`);
      if (el) el.remove();
    };
  }, [name, description, image, price, currency, category, features, rating, reviewCount, url]);

  return null;
};

/**
 * FAQStructuredData - Generates FAQPage schema
 */
interface FAQItem {
  question: string;
  answer: string;
}

export const FAQStructuredData: React.FC<{ faqs: FAQItem[]; pageUrl: string }> = ({ faqs, pageUrl }) => {
  useEffect(() => {
    const existingScript = document.querySelector('script[data-seo="faq"]');
    if (existingScript) existingScript.remove();

    const faqSchema = sanitizeStructuredData({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${BASE_URL}${pageUrl}#faq`,
      "name": "Dialgia Group FAQ | Answers on Delivery, Pricing & Support",
      "description": "Find answers on Dialgia's delivery model, pricing, support, security, and how we build enterprise websites, SaaS products, and AI agents.",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      })),
      "inLanguage": "en-GB"
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'faq');
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-seo="faq"]');
      if (el) el.remove();
    };
  }, [faqs, pageUrl]);

  return null;
};
